// ==========================================================================
// User Service - API calls for user management
// ==========================================================================

import api from '@/lib/axios';
import type {
  CreateUserRequest,
  CreateUserResponse,
  VerifyMagicLinkResponse,
  GetUserResponse,
  UpdateLocationRequest,
  UpdateLocationResponse,
  UpdateUserNameResponse,
  AcceptTosResponse,
  User,
} from '@/types/user';
import { clearGeneratedLinkToken, getGeneratedLinkToken } from '@/lib/generatedLinkStorage';

// Admin token for creating users (temporary - will be handled by backend later)
const ADMIN_TOKEN = 'YXjPg6AHLPfjAoKoXZuYpM7Wh9gsQKXa';

/**
 * Create a new user (admin route - requires admin token)
 * POST /api/user
 */
export async function createUser(data: CreateUserRequest): Promise<CreateUserResponse> {
  const response = await api.post<CreateUserResponse>('/user', data, {
    headers: {
      'Authorization': `Bearer ${ADMIN_TOKEN}`,
    },
  });
  return response.data;
}

/**
 * Validate magic link token and get access token (ENG-5)
 * POST /api/auth/magic-link/verify
 */
export async function verifyMagicLink(token: string): Promise<VerifyMagicLinkResponse> {
  const generatedLinkToken = getGeneratedLinkToken() ?? undefined;

  const response = await api.post<VerifyMagicLinkResponse>('/auth/magic-link/verify', {
    token,
    ...(generatedLinkToken ? { generated_link_token: generatedLinkToken } : {}),
  });

  // First-touch attribution: the slug only converts once. Drop it once it's
  // ridden along on a successful verify.
  if (generatedLinkToken) {
    clearGeneratedLinkToken();
  }

  return response.data;
}

/**
 * Get current user info
 * GET /api/user
 */
export async function getUser(): Promise<User> {
  const response = await api.get<GetUserResponse>('/user');
  return response.data.data;
}

/**
 * Accept Terms of Service (sets onboarded: true)
 * POST /api/user/tos
 */
export async function acceptTos(): Promise<User> {
  const response = await api.post<AcceptTosResponse>('/user/tos');
  return response.data.data;
}

/**
 * Update/confirm user location (sets confirmed: true, verified: true)
 * PUT /api/user/location
 */
export async function updateLocation(data: UpdateLocationRequest): Promise<User | null> {
  const response = await api.put<UpdateLocationResponse>('/user/location', data);

  // Handle different response structures
  if (response.data?.data) {
    return response.data.data;
  }
  // Maybe the API returns the user directly
  if (response.data && 'id' in response.data) {
    return response.data as unknown as User;
  }
  // Return null if we can't parse the response
  return null;
}

/**
 * Update user name
 * PUT /api/user
 */
export async function updateUserName(name: string): Promise<User> {
  const response = await api.put<UpdateUserNameResponse>('/user', { name });
  return response.data.data;
}

/**
 * Extract token from magic link URL
 * Example: "http://yaya-engine.test/auth/TOKEN_HERE" → "TOKEN_HERE"
 */
export function extractTokenFromMagicLink(magicLink: string): string {
  const parts = magicLink.split('/auth/');
  return parts[parts.length - 1] || '';
}

/**
 * Generate a random Kenyan phone number for testing
 */
export function generateRandomPhone(): string {
  const digits = Math.floor(100000000 + Math.random() * 900000000);
  return `+254${digits}`;
}
